import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { formatEUR, formatDate, statusClass } from "@/lib/format";
import { useTwoFactor } from "@/components/TwoFactorProvider";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, ArrowRight } from "lucide-react";

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid={TID.leadsPage} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
            MVP · lead → preventivo → prd
          </div>
          <p className="text-sm text-neutral-400 mt-1 max-w-2xl">
            Cattura ogni nuovo lead in ingresso. Da qui generi preventivo e PRD in un click
            (con secondo fattore).
          </p>
        </div>
        <Button
          data-testid={TID.leadNewBtn}
          onClick={() => setFormOpen(true)}
          className="rounded-none bg-[#EDEDED] text-black hover:bg-white font-mono text-xs uppercase tracking-widest px-4"
        >
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Nuovo Lead
        </Button>
      </div>

      {err && (
        <div className="border border-red-900/60 bg-red-950/20 px-4 py-3 font-mono text-xs text-red-300">
          {err}
        </div>
      )}

      <div className="border border-[#1F1F1F] bg-[#0C0C0C]">
        <header className="border-b border-[#1F1F1F] px-4 py-3 flex items-center justify-between bg-[#141414]">
          <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-400">
            lead in pipeline
          </span>
          <span className="font-mono text-[10px] text-neutral-600">{leads.length} items</span>
        </header>
        <table data-testid={TID.leadsTable} className="w-full">
          <thead>
            <tr className="border-b border-[#1F1F1F] font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">
              <th className="text-left px-4 py-2">ID</th>
              <th className="text-left px-4 py-2">Azienda</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Settore</th>
              <th className="text-right px-4 py-2">Budget</th>
              <th className="text-left px-4 py-2">Stato</th>
              <th className="text-left px-4 py-2 hidden lg:table-cell">Creato</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1F1F1F]">
            {loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 font-mono text-xs text-neutral-500">
                  <span className="cursor-blink">LOADING</span>
                </td>
              </tr>
            )}
            {!loading && leads.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-6 font-mono text-xs text-neutral-500">
                  Nessun lead. Clicca <span className="text-neutral-300">Nuovo Lead</span>.
                </td>
              </tr>
            )}
            {leads.map((l) => (
              <tr
                key={l.id}
                data-testid={TID.leadRow}
                className="hover:bg-[#141414]"
              >
                <td className="px-4 py-3 font-mono text-xs text-neutral-300">{l.id}</td>
                <td className="px-4 py-3 text-sm">
                  <Link to={`/app/leads/${l.id}`} className="hover:text-[#EDEDED] text-[#EDEDED] underline decoration-neutral-700 hover:decoration-[#EDEDED] underline-offset-2">
                    {l.company_name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-sm text-neutral-400 hidden md:table-cell">
                  {l.sector}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-right">
                  {formatEUR(l.budget_eur)}
                </td>
                <td className="px-4 py-3">
                  <span className={`font-mono text-[9px] uppercase tracking-widest border px-1.5 py-0.5 ${statusClass(l.status)}`}>
                    {l.status}
                  </span>
                </td>
                <td className="px-4 py-3 font-mono text-[11px] text-neutral-500 hidden lg:table-cell">
                  {formatDate(l.created_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/app/leads/${l.id}`}
                    className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#EDEDED]"
                  >
                    Apri
                    <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <NewLeadDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        onCreated={() => {
          setFormOpen(false);
          load();
        }}
      />
    </div>
  );
}

function NewLeadDialog({ open, onOpenChange, onCreated }) {
  const twoFactor = useTwoFactor();
  const [form, setForm] = useState({
    company_name: "",
    sector: "",
    need: "",
    budget_eur: "",
    contact_email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const canSubmit =
    form.company_name.trim() && form.sector.trim() && form.need.trim() && form.budget_eur !== "";

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr("");
    try {
      await twoFactor.requireCode({
        actionLabel: `Crea lead: ${form.company_name}`,
        run: async (code) => {
          const { data } = await api.post(
            "/leads",
            {
              company_name: form.company_name.trim(),
              sector: form.sector.trim(),
              need: form.need.trim(),
              budget_eur: Number(form.budget_eur),
              contact_email: form.contact_email.trim() || null,
            },
            { headers: { "X-Second-Factor": code } },
          );
          toast.success(`Lead ${data.id} creato · ${data.company_name}`);
          setForm({ company_name: "", sector: "", need: "", budget_eur: "", contact_email: "" });
          onCreated?.();
        },
      });
    } catch (ex) {
      if (ex?.message !== "cancelled") {
        setErr(ex?.response?.data?.detail || ex.message || "Errore");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TID.leadForm}
        className="rounded-none border border-[#1F1F1F] bg-[#0C0C0C] text-[#EDEDED] max-w-lg p-0"
      >
        <DialogHeader className="border-b border-[#1F1F1F] px-5 py-3 space-y-1">
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
            structura://leads/new
          </div>
          <DialogTitle className="font-heading text-xl uppercase tracking-tight">
            Nuovo Lead
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="px-5 py-5 space-y-4">
          <Field label="Azienda">
            <Input
              data-testid={TID.leadFormCompany}
              value={form.company_name}
              onChange={set("company_name")}
              placeholder="Es. IntimoAround"
              className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-0 focus-visible:border-[#EDEDED]"
            />
          </Field>
          <Field label="Settore">
            <Input
              data-testid={TID.leadFormSector}
              value={form.sector}
              onChange={set("sector")}
              placeholder="Es. E-commerce · Lingerie"
              className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-0 focus-visible:border-[#EDEDED]"
            />
          </Field>
          <Field label="Esigenza">
            <Textarea
              data-testid={TID.leadFormNeed}
              value={form.need}
              onChange={set("need")}
              placeholder="Descrivi il bisogno..."
              rows={4}
              className="rounded-none border-[#1F1F1F] bg-[#050505] focus-visible:ring-0 focus-visible:border-[#EDEDED] resize-none"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Budget €">
              <Input
                data-testid={TID.leadFormBudget}
                type="number"
                min="0"
                step="500"
                value={form.budget_eur}
                onChange={set("budget_eur")}
                placeholder="18000"
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-0 focus-visible:border-[#EDEDED]"
              />
            </Field>
            <Field label="Email contatto (opt.)">
              <Input
                data-testid={TID.leadFormEmail}
                type="email"
                value={form.contact_email}
                onChange={set("contact_email")}
                placeholder="founder@example.com"
                className="rounded-none border-[#1F1F1F] bg-[#050505] font-mono focus-visible:ring-0 focus-visible:border-[#EDEDED]"
              />
            </Field>
          </div>

          {err && (
            <div className="font-mono text-xs text-red-400 border border-red-900/60 bg-red-950/20 px-3 py-2">
              {err}
            </div>
          )}

          <div className="font-mono text-[10px] text-neutral-500 pt-2">
            → la creazione richiede il secondo fattore (scrittura)
          </div>
        </form>

        <DialogFooter className="border-t border-[#1F1F1F] px-5 py-3 gap-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-none font-mono text-xs uppercase tracking-widest hover:bg-[#141414]"
          >
            Annulla
          </Button>
          <Button
            data-testid={TID.leadFormSubmit}
            onClick={submit}
            disabled={!canSubmit || submitting}
            className="rounded-none bg-[#EDEDED] text-black hover:bg-white disabled:opacity-40 font-mono text-xs uppercase tracking-widest"
          >
            {submitting ? "Creazione…" : "Crea Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-neutral-500">
        {label}
      </label>
      {children}
    </div>
  );
}
